import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import ExcelImportButton from './excelImportButton.component';
import Button from '../button.component';
import Spinner from '../spinner.component';

import {
  Modal as AppkitModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@pwc/appkit-react/lib/Modal';

const ANIMATION_TIME = 700;

const Modal = receivedProps => {
  const [props, setProps] = useState(receivedProps);
  const isClosing = useRef(false);

  useEffect(() => {
    if (props.isVisible && !receivedProps.isVisible) {
      isClosing.current = true;
      setTimeout(() => {
        isClosing.current = false;
        setProps(receivedProps);
      }, ANIMATION_TIME);
    } else if (!isClosing.current) {
      setProps(receivedProps);
    }
  }, [receivedProps]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    children,
    className,
    title,
    isBusy,
    closeAction,
    dismissText,
    dismissAction,
    submitText,
    submitAction,
    submitDisabled,
    submitAndAddAnotherText,
    submitAndAddAnotherAction,
    withUpload,
    submitButtonAdditionalProps,
    isLoading,
    isSubmitting,
    cancelAsPrimary,
    leftButtonText,
    leftButtonAction,
  } = props;

  const SubmitButtonComponent = withUpload ? ExcelImportButton : Button;

  return (
    <AppkitModal visible={receivedProps.isVisible} onCancel={closeAction} className={className} zIndex={100000}>
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>
        {isLoading ? <Spinner /> : children}
      </ModalBody>
      <ModalFooter>
        {(leftButtonText && leftButtonAction)
        && (
          <Button
            kind="secondary"
            className="mr-auto"
            onClick={leftButtonAction}
          >
            {leftButtonText}
          </Button>
        )
        }
        {(submitAndAddAnotherText && submitAndAddAnotherAction)
          && (
            <SubmitButtonComponent
              kind="primary"
              type="submit"
              negative
              className="mr-auto"
              isLoading={isSubmitting}
              onClick={submitAndAddAnotherAction}
              disabled={isBusy || submitDisabled}
            >
              {submitAndAddAnotherText}
            </SubmitButtonComponent>
          )
        }
        {(dismissText && dismissAction)
          ? (
            <Button
              kind={cancelAsPrimary ? 'primary' : 'secondary'}
              onClick={dismissAction}
              disabled={isBusy}
            >
              {dismissText}
            </Button>
          )
          : null
        }
        {(submitText && submitAction)
          ? (
            <SubmitButtonComponent
              kind={cancelAsPrimary ? 'secondary' : 'primary'}
              type="submit"
              isLoading={isSubmitting}
              onClick={submitAction}
              disabled={isBusy || submitDisabled}
              {...submitButtonAdditionalProps}
            >
              {submitText}
            </SubmitButtonComponent>
          )
          : null
        }
      </ModalFooter>
    </AppkitModal>
  );
};

Modal.defaultProps = {
  className: '',
  dismissText: '',
  isBusy: false,
  submitText: '',
  title: '',
  dismissAction: null,
  submitAction: null,
  submitDisabled: false,
  submitAndAddAnotherText: '',
  submitAndAddAnotherAction: null,
  withUpload: false,
  isLoading: false,
  isSubmitting: false,
  isVisible: true,
};

Modal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  className: PropTypes.string.isRequired,
  dismissText: PropTypes.node,
  isBusy: PropTypes.bool,
  submitText: PropTypes.node,
  title: PropTypes.string,
  closeAction: PropTypes.func.isRequired,
  dismissAction: PropTypes.func,
  submitAction: PropTypes.func,
  submitDisabled: PropTypes.bool,
  submitAndAddAnotherText: PropTypes.node,
  submitAndAddAnotherAction: PropTypes.func,
  withUpload: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isVisible: PropTypes.bool,
  cancelAsPrimary: PropTypes.bool,
  submitButtonAdditionalProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  leftButtonText: PropTypes.string,
  leftButtonAction: PropTypes.func,
};

export default Modal;
